import api from '../../lib/api';
import type { FormField } from '../surveys/types';

export interface FormTemplateFieldGroup {
  id: string;
  name: string;
  weight?: number;
  order?: number;
}

export interface FormTemplateStructure {
  fieldGroups?: FormTemplateFieldGroup[];
  fields: FormField[];
  captureSettings?: FormTemplateCaptureSettings;
}

export interface FormTemplateCaptureSettings {
  targetSheetId: string;
  mode: 'appendRow';
  trackingSummaryFields?: string[];
  trackingVisibleFields?: string[];
  requireReviewNoteOnReject?: boolean;
}

export interface FormTemplateDetail {
  id: string;
  code: string;
  name: string;
  description?: string | null;
  displayMode?: 'form' | 'spreadsheet';
  dataSourceCode?: string | null;
  currentVersion?: {
    id: string;
    version: number;
    structure: FormTemplateStructure;
    changelog?: string | null;
  } | null;
  taxonomy?: {
    id: string;
    name: string;
  } | null;
}

export interface LookupOption {
  value: unknown;
  label: string;
  responseId: string;
}

export interface DeleteFormTemplateResponse {
  deletedTemplateId: string;
  deletedCode: string;
  deactivatedAssessmentIds: string[];
}

const MAX_SEARCH_LEN = 128;
const MAX_CODE_LEN = 64;

export async function fetchFormTemplateByCode(code: string): Promise<FormTemplateDetail> {
  const safe = code.slice(0, MAX_CODE_LEN);
  const { data } = await api.get<{ data: FormTemplateDetail }>(
    `form-templates/by-code/${encodeURIComponent(safe)}`,
  );
  return data.data;
}

export async function fetchLookupOptions(params: {
  templateCode: string;
  valueField: string;
  displayField: string;
  search?: string;
  limit?: number;
}): Promise<LookupOption[]> {
  const search = (params.search ?? '').slice(0, MAX_SEARCH_LEN);
  const { data } = await api.get<{ data: LookupOption[] }>(
    `form-templates/${encodeURIComponent(params.templateCode)}/lookup-options`,
    {
      params: {
        valueField: params.valueField,
        displayField: params.displayField,
        search: search || undefined,
        limit: params.limit ?? 50,
      },
    },
  );
  return data.data ?? [];
}

/**
 * Elimina una plantilla por código. Solo superadmin (el backend lo enforce).
 * Devuelve el id del template borrado y los ids de los assessment espejo
 * que quedaron desactivados (soft-delete) para que la UI pueda informarlo.
 */
export async function deleteFormTemplate(code: string): Promise<DeleteFormTemplateResponse> {
  const safe = code.slice(0, MAX_CODE_LEN);
  const { data } = await api.delete<{ success: boolean; data: DeleteFormTemplateResponse }>(
    `form-templates/by-code/${encodeURIComponent(safe)}`,
  );
  return data.data;
}
